'use client'

import { useHyperliquidAgent } from '@/hooks/useHyperliquidAgent'
import { getHyperliquidWS } from '@/lib/realtime/hyperliquid-ws'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { toast } from 'sonner'

type UserEventFill = {
	coin: string
	px: string
	sz: string
	side: 'A' | 'B'
	time: number
	closedPnl?: string
	hash: string
	oid: number
	tid?: number
}

type UserEvent = {
	fills?: UserEventFill[]
	funding?: unknown
	liquidation?: unknown
	nonUserCancel?: unknown
}

/**
 * Subscribes once (per authenticated user) to userEvents over WS.
 * On fill: invalidates positions/balance/open-orders caches so the UI
 * updates within a frame instead of waiting for the 5s poll cycle.
 * Fires a toast with the fill summary.
 */
export function useHyperliquidUserEvents() {
	// Subscribe with the SERVER-validated primary address (returned by the
	// authenticated /api/hyperliquid/agent/init endpoint). Don't use
	// client-side `eoaAddress` from WalletContext — that field is
	// unauthenticated and would let a malicious client subscribe to any
	// HL account's fill stream (leaking PII via the toast text).
	const t = useTranslations('hyperliquid.toast')
	const { status } = useHyperliquidAgent()
	const primaryAddress = status.data?.primaryAddress
	const qc = useQueryClient()

	useEffect(() => {
		if (!primaryAddress) return
		let unsubFn: (() => void) | null = null
		let cancelled = false

		getHyperliquidWS()
			.subscribe({ type: 'userEvents', user: primaryAddress }, data => {
				if (cancelled) return
				const event = data as UserEvent
				if (event.fills?.length) {
					qc.invalidateQueries({ queryKey: ['hl-balance'] })
					qc.invalidateQueries({ queryKey: ['hl-positions'] })
					qc.invalidateQueries({ queryKey: ['hl-open-orders'] })
					qc.invalidateQueries({ queryKey: ['hl-fills'] })
					for (const f of event.fills) {
						const params = {
							size: Number.parseFloat(f.sz).toFixed(2),
							coin: f.coin,
							price: (Number.parseFloat(f.px) * 100).toFixed(1),
						}
						toast.success(f.side === 'B' ? t('filledBuy', params) : t('filledSell', params), {
							id: `hl-fill-${f.tid ?? f.oid}`,
							duration: 4000,
						})
					}
				}
			})
			.then(un => {
				if (cancelled) un()
				else unsubFn = un
			})
			.catch(() => {})

		return () => {
			cancelled = true
			unsubFn?.()
		}
	}, [primaryAddress, qc, t])
}
